import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw,
  Loader2,
  User,
  Copy,
  ExternalLink,
  ChevronDown,
  Check,
  Trophy,
  Home,
  History,
  Wallet,
  Plus,
  Trash2,
  Download
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'main', name: 'Главная', icon: Home, description: 'Баланс и ставки' },
  { id: 'history', name: 'История ставок', icon: History, description: 'Все ставки' },
  { id: 'wallet', name: 'Кошелёк', icon: Wallet, description: 'Пополнения и выводы' },
  { id: 'profile', name: 'Профиль', icon: User, description: 'Личные данные' }
];

// Sports and events for bets
const SPORTS = ['Футбол', 'Хоккей', 'Баскетбол', 'Теннис', 'Киберспорт'];
const TEAMS = {
  'Футбол': [
    { home: 'Спартак', away: 'ЦСКА' },
    { home: 'Зенит', away: 'Локомотив' },
    { home: 'Реал Мадрид', away: 'Барселона' },
    { home: 'Манчестер Сити', away: 'Ливерпуль' },
  ],
  'Хоккей': [
    { home: 'СКА', away: 'ЦСКА' },
    { home: 'Ак Барс', away: 'Авангард' },
  ],
  'Баскетбол': [
    { home: 'ЦСКА', away: 'Зенит' },
    { home: 'Лейкерс', away: 'Бостон' },
  ],
  'Теннис': [
    { home: 'Медведев', away: 'Джокович' },
    { home: 'Рублёв', away: 'Алькараз' },
  ],
  'Киберспорт': [
    { home: 'NaVi', away: 'G2' },
    { home: 'Team Spirit', away: 'OG' },
  ],
};

export default function LigaStavokTemplate() {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('main');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Custom bets - will be auto-generated when profile is selected
  const [bets, setBets] = useState([]);
  // Transactions (deposits/withdrawals)
  const [transactions, setTransactions] = useState([]);
  // Mix operations checkbox
  const [mixOperations, setMixOperations] = useState(true);
  // Archive download state
  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);
  // Browser settings
  const [showBrowser, setShowBrowser] = useState(true);
  const [browserTheme, setBrowserTheme] = useState('light');

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  // Generate transactions from OZON profile
  // OZON deposit (пополнение в OZON) → Liga Stavok withdrawal (вывод из букмекера)
  // OZON withdrawal (вывод из OZON) → Liga Stavok deposit (пополнение букмекера)
  const generateTransactions = (profile, shouldMix = true) => {
    const ozonTransactions = profile?.transactions || [];
    const newTransactions = [];
    
    // Определить метод по категории OZON
    const getPaymentMethod = (category) => {
      const sbpCategories = ['Переводы', 'Пополнения', 'Перевод'];
      if (sbpCategories.some(c => category?.includes(c))) {
        return 'СБП';
      }
      return 'Карта';
    };
    
    // OZON Deposits → Liga Stavok Withdrawals (выводы)
    const deposits = ozonTransactions.filter(t => t.type === 'deposit');
    deposits.forEach((tx, i) => {
      const method = getPaymentMethod(tx.category);
      newTransactions.push({
        id: tx.id || Date.now() + i,
        type: 'withdrawal',
        description: 'Вывод средств',
        method: method,
        sender: tx.method || 'Получатель',
        amount: tx.amount,
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // OZON Withdrawals → Liga Stavok Deposits (пополнения)
    const withdrawals = ozonTransactions.filter(t => t.type === 'withdrawal');
    withdrawals.forEach((tx, i) => {
      const method = getPaymentMethod(tx.category);
      newTransactions.push({
        id: tx.id || Date.now() + 500 + i,
        type: 'deposit',
        description: 'Пополнение счёта',
        method: method,
        sender: tx.method || 'Отправитель',
        amount: tx.amount,
        status: 'completed',
        date: tx.date && tx.time ? `${tx.date}, ${tx.time}` : ''
      });
    });
    
    // Sort by date
    const parseDate = (dateStr) => {
      if (!dateStr) return new Date(0);
      const match = dateStr.match(/(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (match) {
        const [, day, month, year, hour, min] = match;
        return new Date(year, month - 1, day, hour, min);
      }
      return new Date(0);
    };
    
    newTransactions.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    // If mix is enabled, add random betting operations between transactions
    let finalTransactions = [];
    if (shouldMix && newTransactions.length > 1) {
      const bettingOps = [
        { desc: 'Ставка выиграла', type: 'deposit' },
        { desc: 'Ставка проиграла', type: 'withdrawal' },
        { desc: 'Экспресс выиграл', type: 'deposit' },
        { desc: 'Возврат ставки', type: 'deposit' },
      ];
      
      for (let i = 0; i < newTransactions.length; i++) {
        finalTransactions.push(newTransactions[i]);
        
        // Add random betting op between transactions
        if (i < newTransactions.length - 1 && Math.random() > 0.3) {
          const op = bettingOps[Math.floor(Math.random() * bettingOps.length)];
          const amount = Math.floor(Math.random() * 5000) + 500;
          
          // Generate date between current and next transaction
          const date1 = parseDate(newTransactions[i].date);
          const date2 = parseDate(newTransactions[i + 1].date);
          const midTime = date2.getTime() + Math.random() * (date1.getTime() - date2.getTime());
          const midDate = new Date(midTime);
          const day = String(midDate.getDate()).padStart(2, '0');
          const month = String(midDate.getMonth() + 1).padStart(2, '0');
          const year = midDate.getFullYear();
          const hour = String(midDate.getHours()).padStart(2, '0');
          const min = String(midDate.getMinutes()).padStart(2, '0');
          
          finalTransactions.push({
            id: Date.now() + 1000 + i,
            type: op.type,
            description: op.desc,
            method: 'Ставка',
            sender: 'Букмекер',
            amount: amount,
            status: 'completed',
            date: `${day}.${month}.${year}, ${hour}:${min}`
          });
        }
      }
    } else {
      finalTransactions = [...newTransactions];
    }
    
    // ОБЯЗАТЕЛЬНО: Добавляем первое пополнение СБП в конец (самая ранняя дата - декабрь 2025)
    const randomDay = Math.floor(Math.random() * 28) + 1; // 1-28
    const randomHour = String(Math.floor(Math.random() * 14) + 8).padStart(2, '0'); // 08-22
    const randomMin = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    const firstDepositDate = `${String(randomDay).padStart(2, '0')}.12.2025, ${randomHour}:${randomMin}`;
    const firstDepositAmount = Math.floor(Math.random() * 3501) + 2500; // 2500-6000
    
    finalTransactions.push({
      id: Date.now() + 9999,
      type: 'deposit',
      description: 'Пополнение по СБП',
      method: 'СБП',
      sender: 'Первое пополнение',
      amount: firstDepositAmount,
      status: 'completed',
      date: firstDepositDate
    });
    
    return finalTransactions;
  };

  // Generate random bets automatically
  const generateRandomBets = () => {
    const numBets = Math.floor(Math.random() * 6) + 5; // 5-10 bets
    const newBets = [];
    const betTypes = ['П1', 'X', 'П2', 'ТБ 2.5', 'ТМ 2.5', 'Обе забьют', 'Фора 1', 'Фора 2'];
    
    for (let i = 0; i < numBets; i++) {
      const sport = SPORTS[Math.floor(Math.random() * SPORTS.length)];
      const teams = TEAMS[sport];
      const match = teams[Math.floor(Math.random() * teams.length)];
      
      // ~70% wins, ~30% losses
      const isWin = Math.random() > 0.3;
      
      // Wins: larger amounts (3000-10000), Losses: smaller amounts (500-3000)
      const amount = isWin 
        ? Math.floor(Math.random() * 7000) + 3000  // 3000-10000
        : Math.floor(Math.random() * 2500) + 500;  // 500-3000
      
      newBets.push({
        id: Date.now() + i,
        sport,
        event: `${match.home} - ${match.away}`,
        betType: betTypes[Math.floor(Math.random() * betTypes.length)],
        odds: (1.5 + Math.random() * 3).toFixed(2),
        amount: String(amount),
        result: isWin ? 'win' : 'lose',
        date: ''
      });
    }
    
    return newBets;
  };

  // Auto-generate bets when profile is selected
  const handleSelectProfile = (profile) => {
    setSelectedProfile(profile);
    setDropdownOpen(false);
    // Generate random bets automatically
    const randomBets = generateRandomBets();
    setBets(randomBets);
    // Generate transactions from profile (with current mixOperations state)
    const profileTransactions = generateTransactions(profile, mixOperations);
    setTransactions(profileTransactions);
    toast.success(`${randomBets.length} ставок, ${profileTransactions.length} операций`);
  };

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ozon/profiles`, {
        headers: getAuthHeaders(),
      });
      setProfiles(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Regenerate all bets randomly
  const regenerateBets = () => {
    const randomBets = generateRandomBets();
    setBets(randomBets);
    toast.success(`Сгенерировано ${randomBets.length} новых ставок`);
  };

  const addBet = () => {
    if (bets.length >= 15) {
      toast.error("Максимум 15 ставок");
      return;
    }
    const sport = SPORTS[Math.floor(Math.random() * SPORTS.length)];
    const teams = TEAMS[sport];
    const match = teams[Math.floor(Math.random() * teams.length)];
    const betTypes = ['П1', 'X', 'П2', 'ТБ 2.5', 'ТМ 2.5', 'Обе забьют'];
    const isWin = Math.random() > 0.3;
    const amount = isWin 
      ? Math.floor(Math.random() * 7000) + 3000
      : Math.floor(Math.random() * 2500) + 500;
    
    setBets([...bets, {
      id: Date.now(),
      sport,
      event: `${match.home} - ${match.away}`,
      betType: betTypes[Math.floor(Math.random() * betTypes.length)],
      odds: (1.5 + Math.random() * 3).toFixed(2),
      amount: String(amount),
      result: isWin ? 'win' : 'lose',
      date: ''
    }]);
  };

  const removeBet = (id) => {
    if (bets.length <= 1) return;
    setBets(bets.filter(b => b.id !== id));
  };

  const updateBet = (id, field, value) => {
    setBets(bets.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const generateRandomDate = () => {
    const now = new Date();
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date(now - daysAgo * 24 * 60 * 60 * 1000);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleGenerate = () => {
    if (!selectedProfile) {
      toast.error("Выберите профиль OZON");
      return;
    }
    
    setIsGenerating(true);
    
    const firstName = selectedProfile.first_name || selectedProfile.name?.split(' ')[0] || "Имя";
    const lastName = selectedProfile.last_name || selectedProfile.name?.split(' ')[1] || "Фамилия";
    const phone = selectedProfile.phone || "+7 (999) 123-45-67";
    const email = selectedProfile.email !== "не установлен" ? selectedProfile.email : `${firstName.toLowerCase()}@mail.ru`;
    
    const generatedBets = bets.map(bet => ({
      ...bet,
      date: bet.date || generateRandomDate(),
      payout: bet.result === 'win' ? (parseFloat(bet.amount) * parseFloat(bet.odds)).toFixed(0) : '0'
    }));
    
    const totalWins = generatedBets.filter(b => b.result === 'win').length;
    const totalBets = generatedBets.length;
    const balance = selectedProfile.template_balance || selectedProfile.balance || Math.floor(Math.random() * 50000) + 10000;
    
    // Calculate date range from transactions
    let dateFrom = null;
    let dateTo = null;
    if (transactions.length > 0) {
      const dates = transactions
        .map(tx => {
          if (!tx.date) return null;
          const match = tx.date.match(/(\d{2})\.(\d{2})\.(\d{4})/);
          if (!match) return null;
          const [, day, month, year] = match;
          return { 
            original: `${day}.${month}.${year}`, 
            sortValue: parseInt(year) * 10000 + parseInt(month) * 100 + parseInt(day) 
          };
        })
        .filter(d => d !== null);
      
      if (dates.length > 0) {
        dates.sort((a, b) => a.sortValue - b.sortValue);
        dateFrom = dates[0].original;
        dateTo = dates[dates.length - 1].original;
      }
    }
    
    const data = {
      id: selectedProfile.id,
      userId: selectedProfile.ozon_id || Math.floor(100000 + Math.random() * 900000),
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      phone,
      email,
      balance,
      bets: generatedBets,
      transactions: transactions,
      verified: true,
      totalBets,
      totalWins,
      winRate: Math.round((totalWins / totalBets) * 100) + '%',
      showBrowser,
      browserTheme,
      dateFrom,
      dateTo
    };
    
    localStorage.setItem('ligastavok_preview_data', JSON.stringify(data));
    
    setGeneratedData(data);
    setIsGenerating(false);
    toast.success("Данные сгенерированы!");
  };

  const getPreviewUrl = () => {
    if (!generatedData) return '';
    return `${window.location.origin}/templates/ligastavok/${selectedTemplate}`;
  };

  const openPreview = () => {
    const url = getPreviewUrl();
    if (url) window.open(url, '_blank');
  };

  const copyLink = () => {
    const url = getPreviewUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована!");
    }
  };

  const downloadArchive = async () => {
    if (!generatedData) return;
    
    setIsDownloadingArchive(true);
    toast.info("Создание архива скриншотов...");
    
    try {
      const response = await axios.post(
        `${API}/templates/ligastavok/archive`,
        { 
          data: generatedData,
          pages: ['main', 'history', 'wallet', 'profile']
        },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Создаём ссылку для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LigaStavok_${generatedData.userId}_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Архив скриншотов скачан!");
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Ошибка создания архива");
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="ligastavok-template-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-lg">
              <Trophy className="w-5 h-5 text-yellow-400" />
            </div>
            Лига Ставок
          </h1>
          <p className="text-slate-500 mt-1">
            Генератор букмекерского аккаунта
          </p>
        </div>
        <Button onClick={fetchProfiles} variant="outline">
          <RefreshCw className="w-4 h-4" />
          Обновить
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Настройки</h2>
            
            {/* Template Selection */}
            <div className="space-y-3 mb-6">
              <Label>Страница</Label>
              <div className="grid grid-cols-3 gap-2">
                {TEMPLATES.map((template) => {
                  const Icon = template.icon;
                  return (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${selectedTemplate === template.id ? 'text-blue-500' : 'text-slate-400'}`} />
                      <div className={`text-sm font-medium ${selectedTemplate === template.id ? 'text-blue-700' : 'text-slate-700'}`}>
                        {template.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Profile Selection */}
            <div className="space-y-3 mb-6">
              <Label>Профиль OZON</Label>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-3 rounded-lg border-2 border-slate-200 bg-white text-left flex items-center justify-between hover:border-slate-300"
                >
                  {selectedProfile ? (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-slate-800 font-medium">{selectedProfile.name}</div>
                        <div className="text-xs text-slate-500">ID: {selectedProfile.ozon_id}</div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Выберите профиль...</span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-2 py-2 bg-white border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {profiles.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => handleSelectProfile(profile)}
                        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-slate-50"
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{profile.name}</span>
                        {selectedProfile?.id === profile.id && <Check className="w-4 h-4 text-blue-500 ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Mix Operations Checkbox */}
            <div className="flex items-center gap-3 mb-6 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="mixOperations"
                checked={mixOperations}
                onChange={(e) => {
                  setMixOperations(e.target.checked);
                  // Regenerate transactions if profile selected
                  if (selectedProfile) {
                    const profileTransactions = generateTransactions(selectedProfile, e.target.checked);
                    setTransactions(profileTransactions);
                  }
                }}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="mixOperations" className="text-sm text-slate-700 cursor-pointer">
                Перемешивать операции (добавить рандомные ставки)
              </label>
            </div>

            {/* Browser Settings */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="showBrowser"
                  checked={showBrowser}
                  onChange={(e) => setShowBrowser(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="showBrowser" className="text-sm text-slate-700 cursor-pointer">
                  Показывать рамку браузера
                </label>
              </div>
              
              {showBrowser && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500 mb-2 block">Тема браузера</Label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setBrowserTheme('light')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        browserTheme === 'light'
                          ? 'bg-white border-2 border-blue-500 text-blue-700 shadow-sm'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      Светлая
                    </button>
                    <button
                      onClick={() => setBrowserTheme('dark')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        browserTheme === 'dark'
                          ? 'bg-slate-800 border-2 border-blue-500 text-white shadow-sm'
                          : 'bg-slate-700 border border-slate-600 text-slate-200 hover:bg-slate-600'
                      }`}
                    >
                      Тёмная
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={!selectedProfile || isGenerating}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            >
              {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
              Сформировать
            </Button>
          </CardContent>
        </Card>

        {/* Right Panel - Bets Editor */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">История ставок</h2>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={regenerateBets} disabled={!selectedProfile}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Регенерировать
                </Button>
                <Button size="sm" variant="outline" onClick={addBet} disabled={bets.length >= 15}>
                  <Plus className="w-4 h-4 mr-1" /> Добавить
                </Button>
              </div>
            </div>
            
            <div className="space-y-3 max-h-[450px] overflow-y-auto">
              {bets.map((bet, index) => (
                <div key={bet.id} className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700 text-sm">Ставка #{index + 1}</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={bet.result}
                        onChange={(e) => updateBet(bet.id, 'result', e.target.value)}
                        className={`text-xs px-2 py-1 rounded border-0 ${
                          bet.result === 'win' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        <option value="win">Выигрыш</option>
                        <option value="lose">Проигрыш</option>
                      </select>
                      {bets.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => removeBet(bet.id)} className="text-red-500 h-6 w-6 p-0">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={bet.sport}
                      onChange={(e) => {
                        const sport = e.target.value;
                        const teams = TEAMS[sport];
                        const match = teams[0];
                        updateBet(bet.id, 'sport', sport);
                        updateBet(bet.id, 'event', `${match.home} - ${match.away}`);
                      }}
                      className="p-2 rounded border border-slate-200 text-sm"
                    >
                      {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Input
                      placeholder="Коэффициент"
                      value={bet.odds}
                      onChange={(e) => updateBet(bet.id, 'odds', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                  
                  <Input
                    placeholder="Событие (Команда1 - Команда2)"
                    value={bet.event}
                    onChange={(e) => updateBet(bet.id, 'event', e.target.value)}
                    className="text-sm"
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Тип ставки (П1, X, П2...)"
                      value={bet.betType}
                      onChange={(e) => updateBet(bet.id, 'betType', e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Сумма ₽"
                      value={bet.amount}
                      onChange={(e) => updateBet(bet.id, 'amount', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        {generatedData && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Предпросмотр</h2>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                  <span className="text-white font-bold">{generatedData.firstName[0]}{generatedData.lastName[0]}</span>
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{generatedData.fullName}</div>
                  <div className="text-sm text-slate-500">
                    ID: {generatedData.userId} • {generatedData.totalBets} ставок • {generatedData.winRate} побед
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={openPreview} className="bg-blue-600 hover:bg-blue-700">
                    <ExternalLink className="w-4 h-4 mr-1" /> Открыть
                  </Button>
                  <Button variant="outline" onClick={copyLink}>
                    <Copy className="w-4 h-4 mr-1" /> Копировать
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={downloadArchive}
                    disabled={isDownloadingArchive}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    {isDownloadingArchive ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-1" />
                    )}
                    Архив
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
